// Pure geometry for ImageCropDialog. Every rect here is in the pixel space of
// the *rotated* source image (width/height swap at 90° and 270°), so the crop
// rect is exactly the output region before any downscaling.

export type Size = { width: number; height: number };
export type Point = { x: number; y: number };
export type Rect = { x: number; y: number; width: number; height: number };
export type Rotation = 0 | 90 | 180 | 270;
export type ResizeHandle = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

export const MIN_CROP_SIZE = 16;
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 5;
export const MAX_OUTPUT_SIDE = 4096;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), Math.max(min, max));

export const rotatedSize = (natural: Size, rotation: Rotation): Size =>
  rotation % 180 === 0
    ? natural
    : { width: natural.height, height: natural.width };

export const nextRotation = (rotation: Rotation): Rotation =>
  ((rotation + 90) % 360) as Rotation;

/** Largest rect of `aspect` (width / height) centred in `bounds`; the whole
 * bounds when `aspect` is null. */
export const defaultCrop = (bounds: Size, aspect: number | null): Rect => {
  if (!aspect) {
    return { x: 0, y: 0, width: bounds.width, height: bounds.height };
  }
  let width = bounds.width;
  let height = width / aspect;
  if (height > bounds.height) {
    height = bounds.height;
    width = height * aspect;
  }
  return {
    x: (bounds.width - width) / 2,
    y: (bounds.height - height) / 2,
    width,
    height,
  };
};

/** Shrinks the rect to fit `bounds` if needed, then slides it inside. */
export const clampRect = (rect: Rect, bounds: Size): Rect => {
  const width = Math.min(rect.width, bounds.width);
  const height = Math.min(rect.height, bounds.height);
  return {
    x: clamp(rect.x, 0, bounds.width - width),
    y: clamp(rect.y, 0, bounds.height - height),
    width,
    height,
  };
};

export const moveRect = (rect: Rect, delta: Point, bounds: Size): Rect =>
  clampRect({ ...rect, x: rect.x + delta.x, y: rect.y + delta.y }, bounds);

/**
 * Resizes `start` by dragging `handle` by `delta`, keeping the opposite edge
 * (or corner) fixed, staying inside `bounds` and above `minSize`. With an
 * `aspect`, corners follow whichever axis the pointer moved further and edge
 * handles grow the other axis symmetrically around its centre.
 */
export const resizeRect = (
  start: Rect,
  handle: ResizeHandle,
  delta: Point,
  bounds: Size,
  aspect: number | null,
  minSize: number = MIN_CROP_SIZE,
): Rect => {
  const west = handle.includes("w");
  const east = handle.includes("e");
  const north = handle.includes("n");
  const south = handle.includes("s");
  const right = start.x + start.width;
  const bottom = start.y + start.height;

  if (!aspect) {
    let { x, y, width, height } = start;
    if (east) {
      width = clamp(start.width + delta.x, minSize, bounds.width - start.x);
    }
    if (west) {
      x = clamp(start.x + delta.x, 0, right - minSize);
      width = right - x;
    }
    if (south) {
      height = clamp(start.height + delta.y, minSize, bounds.height - start.y);
    }
    if (north) {
      y = clamp(start.y + delta.y, 0, bottom - minSize);
      height = bottom - y;
    }
    return { x, y, width, height };
  }

  const minWidth = Math.max(minSize, minSize * aspect);
  const centerX = start.x + start.width / 2;
  const centerY = start.y + start.height / 2;
  // Room available on each side of the fixed anchor.
  const roomX = east
    ? bounds.width - start.x
    : west
      ? right
      : 2 * Math.min(centerX, bounds.width - centerX);
  const roomY = south
    ? bounds.height - start.y
    : north
      ? bottom
      : 2 * Math.min(centerY, bounds.height - centerY);
  const maxWidth = Math.min(roomX, roomY * aspect);

  const widthFromX = start.width + (east ? delta.x : west ? -delta.x : 0);
  const widthFromY =
    (start.height + (south ? delta.y : north ? -delta.y : 0)) * aspect;
  let proposed: number;
  if ((east || west) && (north || south)) {
    proposed = Math.max(widthFromX, widthFromY);
  } else if (east || west) {
    proposed = widthFromX;
  } else {
    proposed = widthFromY;
  }
  const width = clamp(proposed, Math.min(minWidth, maxWidth), maxWidth);
  const height = width / aspect;

  const x = east ? start.x : west ? right - width : centerX - width / 2;
  const y = south ? start.y : north ? bottom - height : centerY - height / 2;
  return { x, y, width, height };
};

/** Crop to a new aspect ratio: keep the centre, fit inside the old rect's
 * larger dimension, then clamp to bounds. */
export const applyAspect = (
  rect: Rect,
  aspect: number | null,
  bounds: Size,
): Rect => {
  if (!aspect) {
    return rect;
  }
  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;
  let width = Math.max(rect.width, rect.height * aspect);
  let height = width / aspect;
  const fit = defaultCrop(bounds, aspect);
  if (width > fit.width) {
    width = fit.width;
    height = fit.height;
  }
  return clampRect(
    { x: centerX - width / 2, y: centerY - height / 2, width, height },
    bounds,
  );
};

/** Maps a rect through a 90° clockwise turn of an image whose rotated size
 * was `bounds` before the turn. */
export const rotateRectClockwise = (rect: Rect, bounds: Size): Rect => ({
  x: bounds.height - rect.y - rect.height,
  y: rect.x,
  width: rect.height,
  height: rect.width,
});

export const rotatePointClockwise = (point: Point, bounds: Size): Point => ({
  x: bounds.height - point.y,
  y: point.x,
});

/**
 * The affine step that places the *unrotated* source into rotated space:
 * translate by `translate`, then rotate by `angle` radians. Shared by the
 * on-screen CSS transform and the export canvas so they can't disagree.
 */
export const rotationTransform = (
  natural: Size,
  rotation: Rotation,
): { translate: Point; angle: number } => {
  switch (rotation) {
    case 90:
      return { translate: { x: natural.height, y: 0 }, angle: Math.PI / 2 };
    case 180:
      return {
        translate: { x: natural.width, y: natural.height },
        angle: Math.PI,
      };
    case 270:
      return { translate: { x: 0, y: natural.width }, angle: -Math.PI / 2 };
    default:
      return { translate: { x: 0, y: 0 }, angle: 0 };
  }
};

/** Image scale that fits the whole rotated image in the stage at zoom 1. */
export const fitScale = (stage: Size, image: Size, padding = 24): number => {
  const width = Math.max(1, stage.width - padding * 2);
  const height = Math.max(1, stage.height - padding * 2);
  return Math.min(width / image.width, height / image.height);
};

export const clampZoom = (zoom: number): number =>
  clamp(zoom, MIN_ZOOM, MAX_ZOOM);

/** The image point shown at the stage centre may not leave the image. */
export const clampCenter = (center: Point, bounds: Size): Point => ({
  x: clamp(center.x, 0, bounds.width),
  y: clamp(center.y, 0, bounds.height),
});

export type View = { scale: number; center: Point };

export const imageToStage = (point: Point, view: View, stage: Size): Point => ({
  x: (point.x - view.center.x) * view.scale + stage.width / 2,
  y: (point.y - view.center.y) * view.scale + stage.height / 2,
});

export const rectToStage = (rect: Rect, view: View, stage: Size): Rect => {
  const origin = imageToStage(rect, view, stage);
  return {
    x: origin.x,
    y: origin.y,
    width: rect.width * view.scale,
    height: rect.height * view.scale,
  };
};

/** Canvas size for the export: the crop at source resolution, longest side
 * capped at `maxSide`. */
export const outputSize = (
  crop: Rect,
  maxSide: number = MAX_OUTPUT_SIDE,
): Size & { scale: number } => {
  const longest = Math.max(crop.width, crop.height);
  const scale = longest > maxSide ? maxSide / longest : 1;
  return {
    width: Math.max(1, Math.round(crop.width * scale)),
    height: Math.max(1, Math.round(crop.height * scale)),
    scale,
  };
};

export type SourceImageType = "png" | "gif" | "webp" | "svg" | "jpeg" | "other";

export const sniffImageType = (
  bytes: Uint8Array,
  mimeType: string,
): SourceImageType => {
  const startsWith = (sig: number[], offset = 0) =>
    sig.every((byte, i) => bytes[offset + i] === byte);
  if (startsWith([0x89, 0x50, 0x4e, 0x47])) return "png";
  if (startsWith([0x47, 0x49, 0x46, 0x38])) return "gif";
  if (
    startsWith([0x52, 0x49, 0x46, 0x46]) &&
    startsWith([0x57, 0x45, 0x42, 0x50], 8)
  )
    return "webp";
  if (startsWith([0xff, 0xd8, 0xff])) return "jpeg";
  if (mimeType === "image/svg+xml") return "svg";
  if (mimeType === "image/png") return "png";
  if (mimeType === "image/webp") return "webp";
  if (mimeType === "image/gif") return "gif";
  return mimeType === "image/jpeg" ? "jpeg" : "other";
};

/**
 * Whether a WebP file may carry transparency, from its first 32 bytes: plain
 * lossy (`VP8 `) never does, lossless (`VP8L`) and extended (`VP8X`) files
 * say so in a flag. Anything unrecognised counts as alpha, so a transparent
 * image is never flattened onto white.
 */
export const webpHasAlpha = (bytes: Uint8Array): boolean => {
  const chunk = String.fromCharCode(...Array.from(bytes.slice(12, 16)));
  if (chunk === "VP8 ") return false;
  if (chunk === "VP8L" && bytes.length > 24) return (bytes[24] & 0x10) !== 0;
  if (chunk === "VP8X" && bytes.length > 20) return (bytes[20] & 0x10) !== 0;
  return true;
};

export type OutputFormat = {
  mimeType: "image/png" | "image/jpeg" | "image/webp";
  extension: "png" | "jpg" | "webp";
  quality?: number;
};

export const OUTPUT_QUALITY = 0.92;

const PNG: OutputFormat = { mimeType: "image/png", extension: "png" };
const JPEG: OutputFormat = {
  mimeType: "image/jpeg",
  extension: "jpg",
  quality: OUTPUT_QUALITY,
};
const WEBP: OutputFormat = {
  mimeType: "image/webp",
  extension: "webp",
  quality: OUTPUT_QUALITY,
};

/**
 * Export formats in order of preference; the first one `canvas.toBlob`
 * actually produces wins (browsers without a WebP encoder silently return
 * PNG). WebP stays WebP where supported, else PNG only when it may be
 * transparent. PNG and SVG export PNG. GIF exports PNG too: a canvas only
 * holds one frame, so an animated GIF comes out as its first frame.
 */
export const outputFormatsFor = (
  type: SourceImageType,
  hasAlpha = true,
): OutputFormat[] => {
  switch (type) {
    case "webp":
      return [WEBP, hasAlpha ? PNG : JPEG];
    case "png":
    case "gif":
    case "svg":
      return [PNG];
    default:
      return [JPEG];
  }
};

/** Keeps the image point under `anchor` (a stage offset from the stage
 * centre, in px) fixed while the view scale goes from `fromScale` to
 * `toScale`; returns the new view centre in image space. */
export const zoomCenterAround = (
  center: Point,
  anchor: Point,
  fromScale: number,
  toScale: number,
): Point => ({
  x: center.x + anchor.x / fromScale - anchor.x / toScale,
  y: center.y + anchor.y / fromScale - anchor.y / toScale,
});

/** Pixels per line for `WheelEvent.DOM_DELTA_LINE` (Firefox's mouse wheel). */
export const WHEEL_LINE_HEIGHT = 16;

/**
 * A wheel delta in pixels. Firefox reports mouse wheels in lines (mode 1)
 * and some devices in pages (mode 2); left raw, a notch of ~3 lines would
 * zoom about as much as a 3px trackpad nudge.
 */
export const wheelDeltaPixels = (
  delta: number,
  deltaMode: number,
  pageHeight: number,
): number => {
  if (deltaMode === 1) {
    return delta * WHEEL_LINE_HEIGHT;
  }
  if (deltaMode === 2) {
    return delta * pageHeight;
  }
  return delta;
};

/** Output filename: the source URL's basename with the output extension. */
export const outputFileName = (imageUrl: string, extension: string): string => {
  let base = "";
  if (!imageUrl.startsWith("blob:") && !imageUrl.startsWith("data:")) {
    try {
      const path = new URL(imageUrl, "https://placeholder.invalid").pathname;
      base = decodeURIComponent(path.split("/").pop() || "");
    } catch {
      base = "";
    }
  }
  base = base.replace(/\.[^.]*$/, "").trim();
  return `${base || "image"}.${extension}`;
};
